import type {
  MouseEventHandler,
  PropsWithChildren,
  ReactNode,
  Ref,
} from 'react'
import React, { useRef } from 'react'
import { useSyncRefs } from '../hooks/useSyncRefs'
import Menu from '../Menu/Menu'
import { useSelect } from './Select'

export interface OptionProps {
  disabled?: boolean
  displayValue?: string
  children: ReactNode
  value?: string
}

function Option(
  {
    children,
    disabled = false,
    displayValue,
    value = '',
  }: PropsWithChildren<OptionProps>,
  ref: Ref<HTMLButtonElement>,
): JSX.Element {
  const [state] = useSelect()
  const selected = state.value === value

  const internalRef = useRef<HTMLButtonElement>(null)
  const optionRef = useSyncRefs(internalRef, ref, (element) => {
    if (selected && state.displayInputRef.current && element?.textContent) {
      if (state.inputRef.current) {
        state.inputRef.current.value = state.value || ''
      }
      state.displayInputRef.current.value = state.value
        ? displayValue || element.textContent
        : ''
    }
  })

  const handleClick: MouseEventHandler<HTMLButtonElement> = (event) => {
    if (!disabled) {
      const input = state.inputRef.current
      if (input) {
        const descriptor = Object.getOwnPropertyDescriptor(
          window.HTMLInputElement.prototype,
          'value',
        ) as PropertyDescriptor
        const inputEvent = new Event('input', { bubbles: true })

        descriptor.set?.call(input, value)
        input.dispatchEvent(inputEvent)
      }

      const display = state.displayInputRef.current
      if (display) {
        const descriptor = Object.getOwnPropertyDescriptor(
          window.HTMLInputElement.prototype,
          'value',
        ) as PropertyDescriptor
        const inputEvent = new Event('input', { bubbles: true })

        descriptor.set?.call(
          display,
          value ? displayValue || event.currentTarget.textContent : '',
        )
        display.dispatchEvent(inputEvent)
      }
    }
  }

  return (
    <Menu.Item
      disabled={disabled}
      onClick={handleClick}
      ref={optionRef}
      selected={selected}
    >
      {children}
    </Menu.Item>
  )
}

export default Option
